import { components, OptionProps, ValueContainerProps } from "react-select";
import Form from "react-bootstrap/Form";
import classNames from "classnames";
import { SelectOption } from "@idw-types/common";
import { ReactSelectWrapper } from "../react-select-wrapper/react-select-wrapper";
import styles from "./multi-select-form-group.less";
import { cloneElement, useMemo, useState } from "react";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { resources } from "@constants/hardcoded-strings";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { IdwFormLabelWithIconContainer } from './../idw-form-label-with-icon-container/idw-form-label-with-icon-container';
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

type MultiSelectFormGroupProps = {
    controlId: string;
    labelWidth: LabelWidthTypes;
    className?: string;
    automationId: string;
    ariaLabel: string;
    label: string;
    options: Array<SelectOption>;
    onChangeMulti: (options: Array<SelectOption>) => void;
    layout?: LayoutTypes;
    useDefaultValueContainer?: boolean;
    selectedOptions: Array<SelectOption>;
    disabled: boolean;
    isRequired: boolean;
    isSearchable: boolean;
    isClearable: boolean;
    keepMenuOpenOnSelect?: boolean;
    noOptionsMessage?: () => React.ReactNode;
    afterLabelTooltipText?: string;
    afterLabelIcon?: IconDefinition;
    afterLabelIconDisabled?: boolean;
    afterLabelIconOnClick?: () => void;
};

const MultiSelectFormGroupOption = (props: OptionProps<any>) => {
    const { children, ...rest } = props;
    const { onMouseMove, onMouseOver, ...innerPropsRest } = rest.innerProps;
    const newProps = { ...rest, innerProps: innerPropsRest };
    return (
        <components.Option {...newProps} className={styles.multiSelectFormGroupSelectedOption}>
            <span>{children}</span>
            {rest.isSelected && (
                <FontAwesomeIcon className={styles.selectedIcon} icon={faCheck} />
            )}
        </components.Option>
    );
};

const MultiSelectFormGroupValueContainer = (props: ValueContainerProps<any>, displayAllValues: boolean) => {
    const { children, ...rest } = props;
    //@ts-ignore
    const [values, input] = children;
    let valueText = "";
    let additionalValueCount = "";
    if (Array.isArray(values)) {
        const val = (index: number) => values[index].props.children;
        const { length } = values;
        if (displayAllValues) {
            valueText = values.map((_, i) => val(i)).join(", ");
        } else if (length === 1) {
            valueText = `${val(0)}`;
        } else {
            valueText = `${val(0)}`;
            additionalValueCount = `+ ${length - 1} ${resources.common.more}`;
        }
    }

    return (
        <components.ValueContainer {...rest} className={styles.multiSelectFormGroupValueContainer}>
            {valueText && (
                <span className={classNames(styles.firstSelectedOption, additionalValueCount ? styles.multipleOptionsSelected : "")}>
                    {valueText}
                </span>
            )}
            {valueText && additionalValueCount && (
                <span className={styles.additionalText}>
                    {additionalValueCount}
                </span>
            )}
            {!valueText && values}
            {cloneElement(input, { "data-lpignore": "true" })}
        </components.ValueContainer>
    );
};

export const MultiSelectFormGroup = (props: MultiSelectFormGroupProps) => {
    const {
        controlId,
        labelWidth,
        className,
        automationId,
        ariaLabel,
        label,
        options,
        onChangeMulti,
        layout,
        useDefaultValueContainer,
        selectedOptions,
        disabled,
        isRequired,
        isSearchable,
        isClearable,
        keepMenuOpenOnSelect,
        noOptionsMessage,
        afterLabelIcon,
        afterLabelIconDisabled,
        afterLabelIconOnClick,
        afterLabelTooltipText
    } = props;

    const [inputValue, setInputValue] = useState("");

    const onInputChange = (value: string, action: any) => {
        if (action.action === "input-change") {
            setInputValue(value);
        } else if (action.action === "menu-close") {
            setInputValue("");
        }
    };

    const formGroupClassName = useMemo(() => {
        return layout === "column" ? styles.multiSelectFormGroupColumn : styles.multiSelectFormGroupRow;
    }, [layout]);

    const customStyles = {
        option: (provided: any, state: { data: SelectOption }) => ({
            ...provided,
            color: state.data.color || undefined,
            fontStyle: state.data.fontStyle || undefined
        })
    };

    const components = useMemo(() => {
        const initialComponents = {
            Option: MultiSelectFormGroupOption,
            ValueContainer: (props: ValueContainerProps) => MultiSelectFormGroupValueContainer(props, disabled)
        };
    
        if (useDefaultValueContainer) {
            delete initialComponents.ValueContainer;
        }

        return initialComponents;
    }, [useDefaultValueContainer]);

    return (
        <Form.Group controlId={controlId} className={classNames(formGroupClassName, className)}>
           {label && (
                <IdwFormLabelWithIconContainer
                    controlId={controlId}
                    labelWidth={labelWidth}
                    layout={layout}
                    label={label}
                    isRequired={isRequired}
                    afterLabelTooltipText={afterLabelTooltipText}
                    afterLabelIcon={afterLabelIcon}
                    afterLabelIconDisabled={afterLabelIconDisabled}
                    afterLabelIconOnClick={afterLabelIconOnClick}
                />
            )}
            <div className={styles.selectContainer}>
                <Form.Control
                    as={ReactSelectWrapper}
                    // @ts-ignore
                    custom
                    styles={customStyles}
                    isRequired={isRequired}
                    menuPlacement={"auto"}
                    automation-id={automationId}
                    aria-label={ariaLabel}
                    classNamePrefix={"react-select"}
                    options={options}
                    onChange={onChangeMulti as any}
                    menuPortalTarget={document.getElementById("react-select-portal")}
                    inputValue={inputValue}
                    onInputChange={onInputChange}
                    value={selectedOptions as any}
                    isSearchable={!!isSearchable}
                    isClearable={!!isClearable}
                    hideSelectedOptions={false}
                    closeMenuOnSelect={!keepMenuOpenOnSelect}
                    isMulti={true}
                    isDisabled={disabled}
                    autocomplete="off"
                    components={components}
                    noOptionsMessage={noOptionsMessage}
                />
            </div>
        </Form.Group>
    );
};